/**
 * Voyage Data Composable
 * Handles data fetching for voyage details, summary, and telemetry data
 */

import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import SummarySvc from '@/services/SummarySvc';
import moment from 'moment';

export function useVoyageData() {
  /**
   * Fetch voyage summary report by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Voyage summary report data or null
   */
  const fetchVoyageSummaryData = async (voyageId) => {
    try {
      const response = await VoyageSummarySvc.fetchById(voyageId);
      return response?.summary || null;
    } catch (error) {
      console.error('fetchVoyageSummaryData error:', error);
      throw error;
    }
  };

  /**
   * Fetch computed voyage summary metrics by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Summary data or null
   */
  const fetchSummary = async (voyageId) => {
    try {
      return await fetchVoyageSummaryData(voyageId);
    } catch (error) {
      console.error('fetchSummary error:', error);
      return null;
    }
  };

  /**
   * Fetch GPS data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} GPS data or null
   */
  const fetchGpsData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['gps'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.gps || response.data?.gps || response.data || null;
    } catch (error) {
      console.error('fetchGpsData error:', error);
      return null;
    }
  };

  /**
   * Fetch RPM data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} RPM data or null
   */
  const fetchRpmData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['rpm'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.rpm || response.data?.rpm || response.data || null;
    } catch (error) {
      console.error('fetchRpmData error:', error);
      return null;
    }
  };

  /**
   * Fetch flowmeter (fuel consumption) data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} Flowmeter data or null
   */
  const fetchFlowmeterData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['flowmeter'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.flowmeter || response.data?.flowmeter || response.data || null;
    } catch (error) {
      console.error('fetchFlowmeterData error:', error);
      return null;
    }
  };

  /**
   * Load all voyage data (convenience method)
   * @param {Object} context - Context object with voyageId
   * @returns {Promise<Object>} Object with all loaded data
   */
  const loadAllVoyageData = async ({ voyageId }) => {
    const summary = await fetchSummary(voyageId);
    const voyage = summary?.master || null;

    return {
      voyage, summary,
    };
  };

  /**
   * Load telemetry data (GPS, RPM, Flowmeter) for a voyage
   * @param {Object} voyage - Voyage object
   * @returns {Promise<Object>} Object with gpsData, rpmData, flowmeterData
   */
  const loadTelemetryData = async (voyage) => {
    const [gpsData, rpmData, flowmeterData] = await Promise.all([
      fetchGpsData(voyage),
      fetchRpmData(voyage),
      fetchFlowmeterData(voyage),
    ]);

    return { gpsData, rpmData, flowmeterData };
  };

  return {
    // Individual fetch methods
    fetchVoyageSummaryData,
    fetchSummary,
    fetchGpsData,
    fetchRpmData,
    fetchFlowmeterData,
    // Convenience methods
    loadAllVoyageData,
    loadTelemetryData,
  };
}

export default useVoyageData;
